import { useEffect } from 'react';
import { Route, Routes, useLocation, useNavigate, type Location } from 'react-router-dom';

import { PageLayout } from './components/layout/PageLayout';
import { PageContextProvider, usePageContext } from './context/PageContext';
import { Page } from './pages/page/Page';
import { PageModal } from './pages/page/PageModal';
import { type LiveAppPage } from './types/schema/LiveAppPage';

type PageModalWrapperProps = {
  pages: LiveAppPage[];
  homePage: LiveAppPage;
  children: React.ReactNode;
};

// This recursive function gets the top level background location from the current location.
// This is necessary because a modal page can be opened from another modal page, so we need to find the original background location.
function getTopBackgroundLocation(
  location: Location,
  visitedLocations = new Set<string>()
): Location | null {
  if (!location.state?.backgroundLocation) {
    return null;
  }

  const backgroundLocation = location.state.backgroundLocation as Location;

  // Prevent infinite recursion by checking if the background location has already been visited
  if (backgroundLocation.key && visitedLocations.has(backgroundLocation.key)) {
    // eslint-disable-next-line no-console
    console.warn('Cycle detected in getTopBackgroundLocation');
    return backgroundLocation;
  }

  // Add the current background location to the visited locations
  if (backgroundLocation.key) {
    visitedLocations.add(backgroundLocation.key);
  }

  // If the background location has its own background location, we need to go deeper
  if (backgroundLocation.state?.backgroundLocation) {
    return getTopBackgroundLocation(location.state.backgroundLocation);
  }

  // Otherwise, we return the current background location
  return backgroundLocation;
}

export function PageModalWrapper({ pages, homePage, children }: PageModalWrapperProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const { activePage } = usePageContext();

  const backgroundLocation = getTopBackgroundLocation(location);

  // This effect is used to restore the scroll position of the background page, if a page modal was opened from it.
  useEffect(() => {
    if (location.state?.fromModal) {
      window.scrollTo(location.state.scrollX, location.state.scrollY);
    }
  }, [location]);

  if (!activePage?.modal || !backgroundLocation) {
    return children;
  }

  return (
    <>
      {/* Render background page using the background location */}
      <Routes location={backgroundLocation}>
        <Route
          path="/*"
          element={
            <PageContextProvider
              pathname={`${backgroundLocation.pathname}${backgroundLocation.search}`}
              pages={pages}
              homePage={homePage}
              isBackgroundPage
            >
              <PageLayout>
                <Page />
              </PageLayout>
            </PageContextProvider>
          }
        />
      </Routes>

      {/* Render the modal route */}
      <PageModal
        onOpenChange={() => {
          navigate(backgroundLocation, {
            state: {
              fromModal: true,

              // Include the window's scroll position in the state so we can restore it after closing the modal
              scrollY: window.scrollY,
              scrollX: window.scrollX
            }
          });
        }}
      >
        {children}
      </PageModal>
    </>
  );
}
